/**
 * Dashboard Data Hooks
 *
 * Custom React hooks for managing dashboard data with loading states,
 * error handling, and CRUD operations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  projectsService,
  designsService,
  questionsService,
  candidatesService,
  participantsService,
  resultsService,
  aiService,
} from '../services/api';
import type {
  Project,
  ProjectWithStats,
  CreateProjectInput,
  UpdateProjectInput,
  DesignWithStats,
  CreateDesignInput,
  UpdateDesignInput,
  Question,
  CreateQuestionInput,
  UpdateQuestionInput,
  Candidate,
  CreateCandidateInput,
  UpdateCandidateInput,
  ParticipantWithCandidate,
  InterviewResult,
  ProjectInsights,
  GenerateQuestionsInput,
  GeneratedQuestion,
  ChatMessage,
} from '../types';

// =============================================================================
// useProjects Hook
// =============================================================================

export function useProjects() {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!user) {
      console.log('[useProjects] No user, skipping fetch');
      setLoading(false);
      return;
    }

    console.log('[useProjects] Fetching projects for user:', user.uid);

    try {
      setLoading(true);
      setError(null);
      const { projects } = await projectsService.getAll();
      console.log('[useProjects] Fetched projects:', projects?.length || 0);
      setProjects(projects);
    } catch (err) {
      console.error('[useProjects] Error fetching projects:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch projects'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    console.log('[useProjects] Auth state:', { authLoading, hasUser: !!user });
    // Wait for auth to finish loading before fetching
    if (!authLoading) {
      fetchProjects();
    }
  }, [authLoading, user, fetchProjects]);

  const createProject = async (data: CreateProjectInput) => {
    const newProject = await projectsService.create(data);
    // Refetch to get stats
    await fetchProjects();
    return newProject;
  };

  const updateProject = async (id: string, data: UpdateProjectInput) => {
    await projectsService.update(id, data);
    setProjects((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...data } : p))
    );
  };

  const deleteProject = async (id: string) => {
    await projectsService.delete(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    createProject,
    updateProject,
    deleteProject,
  };
}

// =============================================================================
// useProject Hook (single project with designs)
// =============================================================================

export function useProject(projectId: string | null) {
  const [project, setProject] = useState<Project | null>(null);
  const [designs, setDesigns] = useState<DesignWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setDesigns([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { project, designs } = await projectsService.getById(projectId);
      setProject(project);
      setDesigns(designs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch project'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const createDesign = async (data: CreateDesignInput) => {
    if (!projectId) throw new Error('No project selected');
    const newDesign = await projectsService.createDesign(projectId, data);
    await fetchProject();
    return newDesign;
  };

  return {
    project,
    designs,
    loading,
    error,
    refetch: fetchProject,
    createDesign,
  };
}

// =============================================================================
// useDesign Hook (single design with questions)
// =============================================================================

export function useDesign(designId: string | null) {
  const [design, setDesign] = useState<DesignWithStats | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDesign = useCallback(async () => {
    if (!designId) {
      setDesign(null);
      setQuestions([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { design, questions } = await designsService.getById(designId);
      setDesign(design);
      setQuestions(questions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch design'));
    } finally {
      setLoading(false);
    }
  }, [designId]);

  useEffect(() => {
    fetchDesign();
  }, [fetchDesign]);

  const updateDesign = async (data: UpdateDesignInput) => {
    if (!designId) throw new Error('No design selected');
    await designsService.update(designId, data);
    setDesign((prev) => (prev ? { ...prev, ...data } : null));
  };

  const deleteDesign = async (targetDesignId?: string) => {
    const idToDelete = targetDesignId || designId;
    if (!idToDelete) throw new Error('No design selected');
    await designsService.delete(idToDelete);
  };

  const addQuestion = async (data: CreateQuestionInput) => {
    if (!designId) throw new Error('No design selected');
    const newQuestion = await designsService.addQuestion(designId, data);
    setQuestions((prev) => [...prev, newQuestion]);
    return newQuestion;
  };

  const updateQuestion = async (questionId: string, data: UpdateQuestionInput) => {
    await questionsService.update(questionId, data);
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, ...data } : q))
    );
  };

  const deleteQuestion = async (questionId: string) => {
    await questionsService.delete(questionId);
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const reorderQuestions = async (orderedIds: string[]) => {
    if (!designId) throw new Error('No design selected');
    const reorderedQuestions = orderedIds.map((id, index) => ({ id, order: index }));
    await designsService.reorderQuestions(designId, { questions: reorderedQuestions });
    setQuestions((prev) => {
      const questionMap = new Map(prev.map((q) => [q.id, q]));
      return orderedIds
        .map((id, index) => {
          const q = questionMap.get(id);
          return q ? { ...q, order: index } : null;
        })
        .filter((q): q is Question => q !== null);
    });
  };

  return {
    design,
    questions,
    loading,
    error,
    refetch: fetchDesign,
    updateDesign,
    deleteDesign,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
  };
}

// =============================================================================
// useCandidates Hook
// =============================================================================

export function useCandidates() {
  const { user, loading: authLoading } = useAuth();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [search, setSearch] = useState('');

  const fetchCandidates = useCallback(async (searchTerm?: string) => {
    // Don't fetch if not authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await candidatesService.getAll({
        search: searchTerm || search,
        limit: 100,
      });
      // API returns 'candidates' not 'items' - handle both for safety
      const candidatesList = (result as unknown as { candidates?: Candidate[] }).candidates || result.items || [];
      setCandidates(candidatesList);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch candidates'));
    } finally {
      setLoading(false);
    }
  }, [user, search]);

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (!authLoading) {
      fetchCandidates();
    }
  }, [authLoading, fetchCandidates]);

  const searchCandidates = (term: string) => {
    setSearch(term);
  };

  const createCandidate = async (data: CreateCandidateInput) => {
    const newCandidate = await candidatesService.create(data);
    setCandidates((prev) => [newCandidate, ...(prev || [])]);
    setTotal((prev) => (prev || 0) + 1);
    return newCandidate;
  };

  const updateCandidate = async (id: string, data: UpdateCandidateInput) => {
    await candidatesService.update(id, data);
    setCandidates((prev) =>
      (prev || []).map((c) => (c.id === id ? { ...c, ...data } : c))
    );
  };

  const deleteCandidate = async (id: string) => {
    await candidatesService.delete(id);
    setCandidates((prev) => (prev || []).filter((c) => c.id !== id));
    setTotal((prev) => Math.max(0, (prev || 0) - 1));
  };

  return {
    candidates,
    total,
    loading,
    error,
    search,
    searchCandidates,
    refetch: fetchCandidates,
    createCandidate,
    updateCandidate,
    deleteCandidate,
  };
}

// =============================================================================
// useParticipants Hook
// =============================================================================

export function useParticipants(projectId: string | null) {
  const [participants, setParticipants] = useState<ParticipantWithCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchParticipants = useCallback(async () => {
    if (!projectId) {
      setParticipants([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await projectsService.getParticipants(projectId);
      const { participants: fetchedParticipants } = response;
      setParticipants(fetchedParticipants || []);
    } catch (err) {
      console.error('[useParticipants] Error fetching participants:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch participants'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const addParticipants = async (designId: string, candidateIds: string[]) => {
    if (!projectId) throw new Error('No project selected');
    const response = await projectsService.addParticipants(projectId, {
      designId,
      candidateIds,
    });
    const { added } = response as { added: number };

    if (added > 0) {
      // Small delay to ensure Firestore has indexed the new documents
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchParticipants();
    }
    return added;
  };

  const removeParticipant = async (participantId: string) => {
    await participantsService.delete(participantId);
    setParticipants((prev) => prev.filter((p) => p.id !== participantId));
  };

  return {
    participants,
    loading,
    error,
    refetch: fetchParticipants,
    addParticipants,
    removeParticipant,
  };
}

// =============================================================================
// useResults Hook
// =============================================================================

export function useResults(projectId: string | null) {
  const [results, setResults] = useState<InterviewResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchResults = useCallback(async () => {
    if (!projectId) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { results } = await projectsService.getResults(projectId);
      setResults(results);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch results'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const getResult = async (resultId: string) => {
    return resultsService.getById(resultId);
  };

  return {
    results,
    loading,
    error,
    refetch: fetchResults,
    getResult,
  };
}

// =============================================================================
// useInsights Hook
// =============================================================================

export function useInsights(projectId: string | null) {
  const [insights, setInsights] = useState<ProjectInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!projectId) {
      setInsights(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { insights } = await projectsService.getInsights(projectId);
      setInsights(insights);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch insights'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const generateInsights = async () => {
    if (!projectId) throw new Error('No project selected');

    try {
      setGenerating(true);
      setError(null);
      const { insights } = await projectsService.generateInsights(projectId);
      setInsights(insights);
      return insights;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate insights');
      setError(error);
      throw error;
    } finally {
      setGenerating(false);
    }
  };

  return {
    insights,
    loading,
    generating,
    error,
    refetch: fetchInsights,
    generateInsights,
  };
}

// =============================================================================
// useAIGenerate Hook
// =============================================================================

export function useAIGenerate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);

  const generateQuestions = async (params: GenerateQuestionsInput) => {
    try {
      setLoading(true);
      setError(null);
      const { questions } = await aiService.generateQuestions(params);
      setGeneratedQuestions(questions);
      return questions;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate questions');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearGenerated = () => {
    setGeneratedQuestions([]);
    setError(null);
  };

  return {
    generateQuestions,
    generatedQuestions,
    loading,
    error,
    clearGenerated,
  };
}

// =============================================================================
// useAIChat Hook
// =============================================================================

export function useAIChat(projectId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Reset messages when project changes
  useEffect(() => {
    setMessages([]);
    setError(null);
  }, [projectId]);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!projectId) throw new Error('No project selected');
      if (!message.trim()) return;

      const userMessage: ChatMessage = { role: 'user', content: message };
      setMessages((prev) => [...prev, userMessage]);
      setLoading(true);
      setError(null);

      try {
        const { reply } = await aiService.chat(projectId, message, messagesRef.current);
        const assistantMessage: ChatMessage = { role: 'assistant', content: reply };
        setMessages((prev) => [...prev, assistantMessage]);
        return reply;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to send message');
        setError(error);
        // Remove the user message on error
        setMessages((prev) => prev.slice(0, -1));
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  return {
    messages,
    sendMessage,
    loading,
    error,
    clearChat,
  };
}

// =============================================================================
// useAnalyzeTranscript Hook
// =============================================================================

export function useAnalyzeTranscript() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const analyzeTranscript = async (resultId: string) => {
    try {
      setLoading(true);
      setError(null);
      const analysis = await aiService.analyzeTranscript({ resultId });
      return analysis;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to analyze transcript');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    analyzeTranscript,
    loading,
    error,
  };
}
